import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';
import type { MicroFeedback, CreateMicroFeedbackInput } from '../types';
import { toast } from '@/shared/lib/toast';

/**
 * Hook para buscar feed público de micro-feedbacks
 */
export function usePublicFeed() {
  return useQuery({
    queryKey: ['feedback-pdi', 'feed'],
    queryFn: async () => {
      const response = await api.get<{ feedbacks: MicroFeedback[]; total: number }>(
        '/feedback-pdi/feed'
      );

      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutos (feed atualiza mais frequente)
    retry: false,
  });
}

/**
 * Hook para buscar micro-feedbacks (com filtros)
 */
export function useMicroFeedbacks(filters?: {
  toEmployeeId?: string;
  type?: 'RECOGNITION' | 'DEVELOPMENT' | 'CORRECTION' | 'ALIGNMENT';
  visibility?: 'PUBLIC' | 'PRIVATE';
}) {
  return useQuery({
    queryKey: ['feedback-pdi', 'micro-feedbacks', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.toEmployeeId) params.append('toEmployeeId', filters.toEmployeeId);
      if (filters?.type) params.append('type', filters.type);
      if (filters?.visibility) params.append('visibility', filters.visibility);

      const response = await api.get<{ feedbacks: MicroFeedback[]; total: number }>(
        `/feedback-pdi/micro-feedbacks?${params.toString()}`
      );

      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    retry: false,
  });
}

/**
 * Hook para criar micro-feedback
 */
export function useCreateMicroFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateMicroFeedbackInput) => {
      const response = await api.post<{ feedback: MicroFeedback }>(
        '/feedback-pdi/micro-feedbacks',
        data
      );
      return response.data.feedback;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'feed'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'micro-feedbacks'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'dashboard'] });
      toast.success('Feedback enviado com sucesso!');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Erro ao enviar feedback');
    },
  });
}
